import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { Box, Button, Divider, Grid, MenuItem, Paper, Typography } from '@mui/material'
import { Select, Switches, TextField } from 'mui-rff'
import { memo } from 'react'
import { Form, FormSpy } from 'react-final-form'
import { OnChange } from 'react-final-form-listeners'
import ImageUploadComponent from 'src/components/FileUpload'
import { AdminPermissionsService } from 'src/services/adminPermissionsService'
import { Lookup } from 'src/types/Lookup'
import { MasterWorkout, MasterWorkoutLookups } from 'src/types/MasterWorkout'

interface CreateOrEditMasterWorkoutFormProps {
	lookups: MasterWorkoutLookups
	initialValues: MasterWorkout
	onSubmit: (value: MasterWorkout) => void
	onValidate: (value: MasterWorkout) => any
}

const CreateOrEditMasterWorkoutForm: React.FC<CreateOrEditMasterWorkoutFormProps> = ({ initialValues, lookups, onSubmit, onValidate }) => {
	const { canMakeProgramPublic } = AdminPermissionsService.instance.masterWorkoutPage
	const { authors, difficultyRatings, weightSelections, tags, workoutTypes, programTypes, focusedMuscleGroups, sessionTimes } = lookups
	const formFields = (form: any) => {
		return {
			col1: [
				{ field: <TextField label="Permalink" name="permalink" required={true} /> },
				{ field: <TextField label="Name of program" name="name" required={true} /> },
				{ field: <TextField multiline type="text" label="Description" name="description" /> },
				{ field: <TextField multiline type="text" label="Weeks" name="weeks" />, size: 6 },
				{ field: <TextField multiline type="text" label="Days per week" name="daysPerWeek" />, size: 6 },
				{
					field: (
						<FormSpy subscription={{ values: true }}>
							{({ values }) => (
								<ImageUploadComponent
									initialFileUrl={values.thumbnailUrl}
									onUploadStatusChange={(id) => {
										form.change('thumbnailId', id)
										form.submit()
									}}
								/>
							)}
						</FormSpy>
					),
				},
				{
					field: (
						<Select name="authorId" label="Author">
							{authors.map((lookup: Lookup) => (
								<MenuItem key={lookup.value} value={lookup.value}>
									{lookup.name}
								</MenuItem>
							))}
						</Select>
					),
				},
				{
					field: (
						<Select name="sessionTime" label="Session Time">
							{sessionTimes.map((lookup: Lookup) => (
								<MenuItem key={lookup.value} value={lookup.value}>
									{lookup.name}
								</MenuItem>
							))}
						</Select>
					),
				},
				{
					field: (
						<Select name="difficultyRating" label="Difficulty Ratings">
							{difficultyRatings.map((item: string) => (
								<MenuItem key={item} value={item}>
									{item}
								</MenuItem>
							))}
						</Select>
					),
				},
				{
					field: (
						<Select name="focusedMuscleGroups" multiple label="Focused Muscle Groups">
							{focusedMuscleGroups.map((lookup: Lookup) => (
								<MenuItem key={lookup.value} value={lookup.value}>
									{lookup.name}
								</MenuItem>
							))}
						</Select>
					),
				},
				{
					field: (
						<Select name="tags" label="Tags" multiple>
							{tags.map((lookup: Lookup) => (
								<MenuItem key={lookup.value} value={lookup.value}>
									{lookup.name}
								</MenuItem>
							))}
						</Select>
					),
				},
				{
					field: (
						<Select name="workoutType" label="Workout Type" multiple>
							{workoutTypes.map((lookup: Lookup) => (
								<MenuItem key={lookup.value} value={lookup.value}>
									{lookup.name}
								</MenuItem>
							))}
						</Select>
					),
				},
				{ field: <TextField multiline type="text" label="Search Keywords" name="searchKeywords" helperText='Use a comma to separate the values. E.g. "squat, leg day, booty"' /> },
			],
			col2: [
				{
					field: (
						<Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
							<Box>Is Public</Box>
							<Switches name="isPublic" disabled={!canMakeProgramPublic} data={{ label: '', value: initialValues.isPublic }} />
						</Box>
					),
				},
				{
					field: (
						<FormSpy subscription={{ values: true }}>
							{({ values }) => {
								return (
									<DatePicker
										label="Published Date"
										value={values.publishedDate || null}
										onChange={(newValue) => form.change('publishedDate', newValue)}
										renderInput={(params) => <TextField {...params} name="publishedDate" type="text" />}
									/>
								);
							}}
						</FormSpy>
					),
				},
				{
					field: (
						<Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
							<Box>Is New</Box>
							<Switches name="isNew" data={{ label: '', value: initialValues.isNew }} />
						</Box>
					),
				},
				{
					field: (
						<Select name="programType" label="Program Type">
							{programTypes.map((lookup: Lookup) => (
								<MenuItem key={lookup.value} value={lookup.value}>
									{lookup.name}
								</MenuItem>
							))}
						</Select>
					),
				},
				{
					field: (
						<Select name="weightSelection" label="Weight Selection">
							{weightSelections.map((lookup: Lookup) => (
								<MenuItem key={lookup.value} value={lookup.value}>
									{lookup.name}
								</MenuItem>
							))}
						</Select>
					),
				},
				{
					field: (
						<Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
							<Box>Enable Main Lifts</Box>
							<Switches name="enableMains" data={{ label: '', value: initialValues.enableMains }} />
						</Box>
					),
				},
				{
					field: (
						<Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
							<Box>Allow user to change Main Lifts</Box>
							<FormSpy subscription={{ values: true }}>
								{({ values }) => <Switches name="enableMainAlts" data={{ label: '', value: initialValues.enableMainAlts }} disabled={!values.enableMains} />}
							</FormSpy>
						</Box>
					),
				},
				{
					field: (
						<Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
							<Box>Enable Retesting</Box>
							<Switches name="enableRetest" data={{ label: '', value: initialValues.enableRetest }} />
						</Box>
					),
				},
				{
					field: (
						<FormSpy subscription={{ values: true }}>
							{({ values }) => <TextField type="number" label="Restest Frequency" name="retestWeekDuration" disabled={!values.enableRetest} />}
						</FormSpy>
					),
				},
				{
					field: (
						<Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
							<Box>Enable Reps per Set</Box>
							<Switches name="enableRepsPerSet" data={{ label: '', value: initialValues.enableRepsPerSet }} />
						</Box>
					),
				},
				{
					field: (
						<Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
							<Box>Enable RIR per Set</Box>
							<Switches name="enableRirPerSet" data={{ label: '', value: initialValues.enableRirPerSet }} />
						</Box>
					),
				},
			],
		}
	}

	return (
		<Form
			onSubmit={onSubmit}
			initialValues={initialValues}
			validate={onValidate}
			subscription={{ initialValues: true }}
			render={({ handleSubmit, form, submitting }) => (
				<form onSubmit={handleSubmit} noValidate>
					<Grid container spacing={5} alignItems="flex-start">
						<Grid item xs={6}>
							<Grid container spacing={2} alignItems="flex-start">
								<Grid item xs={12}>
									<Typography variant="h4">Details</Typography>
								</Grid>
								<Grid item xs={12}>
									<Divider variant="fullWidth" />
								</Grid>
								{formFields(form).col1.map((item, idx) => (
									<Grid key={idx} item xs={item.size || 12}>
										{item.field}
									</Grid>
								))}
							</Grid>
						</Grid>

						<Grid item xs={6}>
							<Grid container spacing={2} alignItems="flex-start">
								<Grid item xs={12}>
									<Typography variant="h4">Settings</Typography>
								</Grid>
								<Grid item xs={12}>
									<Divider variant="fullWidth" />
								</Grid>
								{formFields(form).col2.map((item, idx) => (
									<Grid key={idx} item xs={12}>
										{item.field}
									</Grid>
								))}
							</Grid>
						</Grid>
					</Grid>

					<OnChange name="enableMains">
						{(value, previous) => {
							if (!value) form.change('enableMainAlts', false)
						}}
					</OnChange>

					<OnChange name="enableRetest">
						{(value, previous) => {
							if (!value) form.change('retestWeekDuration', null)
						}}
					</OnChange>

					<FormSpy subscription={{ values: true, pristine: true }}>
						{({ values, pristine }) => {
							// Autosubmit value changes only if the active master workout was not changed
							if (!pristine) onSubmit(values as MasterWorkout)
							return null
						}}
					</FormSpy>
				</form>
			)}
		/>
	)
}

export default memo(CreateOrEditMasterWorkoutForm)
